/**
 * ToolsControls component - Two-level tool selection system
 * Level 1: Mode selection (sketch, edit, ds, cms)
 * Level 2: Tool selection per mode (future implementation)
 */

import {
  createToolsMainMenuInContainer,
  type ToolsMainMenuAPI,
} from "./subcomponents/ToolsMainMenu/index.js";
import {
  createToolsSubMenuInContainer,
  type ToolsSubMenuAPI,
  type SubMenuItem,
} from "./subcomponents/ToolsSubMenu/index.js";
import "./ToolsControls.scss";

export type ToolMode = "sketch" | "edit" | "ds" | "cms";

export interface ToolsControlsConfig {
  readonly onModeChange?: (mode: ToolMode) => void;
  readonly onToolSelect?: (mode: ToolMode, subtool: string) => void;
  readonly onToolSettingsRequest?: (
    mode: ToolMode,
    subtool: string,
    anchor?: HTMLElement | null
  ) => void;
  readonly onLevelChange?: (level: "main" | "sub", mode?: ToolMode) => void;
  readonly initialMode?: ToolMode;
  readonly initialTool?: string; // If provided, open submenu and select it; if missing, select first
  readonly resolveCurrentSubtool?: (mode: ToolMode) => string | null;
  readonly initialEraserSubtool?: "delete" | "delete-clean";
  readonly initialSelectionSubtool?: "marquee" | "lasso";
}

export interface ToolsControlsAPI {
  readonly getElement: () => HTMLElement;
  readonly setMode: (mode: ToolMode) => void;
  readonly setModeAndTool: (mode: ToolMode, subtool?: string | null) => void;
  readonly getMode: () => ToolMode;
  readonly destroy: () => void;
}

/**
 * Creates ToolsControls component with two-level navigation system
 * @param config - Configuration for the tools controls
 * @returns ToolsControls API
 */
export const createToolsControls = (
  config: ToolsControlsConfig = {}
): ToolsControlsAPI => {
  const {
    onModeChange,
    onToolSelect,
    onToolSettingsRequest,
    onLevelChange,
    initialMode = "edit",
    initialTool,
    initialEraserSubtool = "delete",
    initialSelectionSubtool = "marquee",
  } = config;

  let isDestroyed = false;
  let currentMode: ToolMode = initialMode;
  let toolsMainMenuAPI: ToolsMainMenuAPI | null = null;
  let toolsSubMenuAPI: ToolsSubMenuAPI | null = null;
  // Track last selection for persistence
  let lastSelected: { mode: ToolMode; subtool: string | null } = {
    mode: currentMode,
    subtool: null,
  };
  let eraserSubtoolId: "delete" | "delete-clean" = initialEraserSubtool;
  let selectionSubtoolId: "marquee" | "lasso" = initialSelectionSubtool;
  const eraserIds = new Set(["delete", "delete-clean"]);
  const selectionIds = new Set(["marquee", "lasso"]);
  let lastSelectId: string | null = null;
  let lastSelectAt = 0;
  const SELECT_DEBOUNCE_MS = 120;

  // Create container element
  const container = document.createElement("div");
  container.className = "tools-controls";

  /**
   * Initialize the tools controls with Level 1 (main menu)
   */
  const initToolsControls = (): void => {
    try {
      // Create Level 1 - Main Menu
      toolsMainMenuAPI = createToolsMainMenuInContainer(container, {
        onModeSelect: handleModeChange,
        selectedMode: currentMode,
      });

      // If initial tool is specified or mode has submenu, open it immediately
      if (currentMode === "sketch" || currentMode === "edit") {
        // Seed lastSelected; do NOT auto-pick fallback if none provided
        lastSelected = {
          mode: currentMode,
          subtool: initialTool ?? null,
        };
        showSubMenuForMode(currentMode);
        // Only fire selection if an explicit initialTool exists
        if (initialTool) onToolSelect?.(currentMode, initialTool);
      } else {
        onLevelChange?.("main", currentMode);
      }
    } catch (error) {
      console.error("Failed to create ToolsControls component:", error);
      throw error;
    }
  };

  /**
   * Handle mode change from Level 1
   */
  const handleModeChange = (mode: ToolMode): void => {
    if (isDestroyed) return;

    // Update internal currentMode if different
    if (mode !== currentMode) {
      currentMode = mode;
      toolsMainMenuAPI?.setSelectedMode(mode);
    }

    console.log(`Tools: Mode changed to ${mode}`);

    // Notify container about mode changes regardless of same/different
    onModeChange?.(mode);

    // Show submenu for modes that have one (allow re-entry even if same mode)
    if (mode === "sketch" || mode === "edit") {
      showSubMenuForMode(mode);
    }
  };

  /**
   * Define submenu items for modes
   */
  const getSubMenuItems = (mode: ToolMode): readonly SubMenuItem[] => {
    if (mode === "sketch") {
      return [
        { id: "ballpoint", icon: "ballpoint_medium" },
        { id: "marker", icon: "markers_medium" },
        {
          id: eraserSubtoolId,
          icon:
            eraserSubtoolId === "delete-clean"
              ? "erasers_clean"
              : "erasers_eraser",
        },
        {
          id: selectionSubtoolId,
          icon:
            selectionSubtoolId === "lasso" ? "tools_lasso" : "tools_marquee",
        },
      ];
    }
    if (mode === "edit") {
      return [
        { id: "selection", icon: "tools_inspect" },
        { id: "layout", icon: "tools_layout_frame" },
        { id: "text", icon: "tools_text" },
        { id: "shape", icon: "tools_shape_rectangle" },
        { id: "media", icon: "tools_media" },
        { id: "component", icon: "tools_component" },
      ];
    }
    return [];
  };

  // Removed external resolver in reset phase; selection handled in showSubMenuForMode

  /**
   * Ensure the main menu is mounted and visible (used when leaving submenus)
   */
  const ensureMainMenuVisible = (selectedMode: ToolMode): void => {
    if (toolsSubMenuAPI) {
      try {
        toolsSubMenuAPI.destroy();
      } catch {}
      toolsSubMenuAPI = null;
    }

    const mainMenuEl = toolsMainMenuAPI?.getElement();
    if (toolsMainMenuAPI && mainMenuEl) {
      if (!container.contains(mainMenuEl)) {
        container.appendChild(mainMenuEl);
      }
      toolsMainMenuAPI.setSelectedMode(selectedMode);
      return;
    }

    toolsMainMenuAPI = createToolsMainMenuInContainer(container, {
      onModeSelect: handleModeChange,
      selectedMode,
    });
    onLevelChange?.("main", selectedMode);
  };

  /**
   * Replace main menu with submenu for given mode
   */
  const showSubMenuForMode = (mode: ToolMode): void => {
    // Tear down existing submenu before creating a new one to avoid duplicates
    if (toolsSubMenuAPI) {
      try {
        toolsSubMenuAPI.destroy();
      } catch {}
      toolsSubMenuAPI = null;
    }

    // Remove main menu UI but keep API to restore later
    toolsMainMenuAPI?.getElement().remove();
    if (mode === "sketch") {
      if (lastSelected.subtool && eraserIds.has(lastSelected.subtool)) {
        eraserSubtoolId = lastSelected.subtool as "delete" | "delete-clean";
      }
      if (lastSelected.subtool && selectionIds.has(lastSelected.subtool)) {
        selectionSubtoolId = lastSelected.subtool as "marquee" | "lasso";
      }
    }
    // Compute items and default selection (first if none recorded)
    const items = getSubMenuItems(mode);
    const hasId = (id: string | null | undefined) =>
      !!id && items.some((it) => it.id === id);
    let selectedId: string | undefined;
    if (lastSelected.mode === mode && hasId(lastSelected.subtool)) {
      selectedId = lastSelected.subtool as string;
    } else if (items.length > 0) {
      selectedId = items[0]!.id;
      lastSelected = { mode, subtool: selectedId } as any;
    }

    toolsSubMenuAPI = createToolsSubMenuInContainer(container, {
      mode,
      title: mode,
      items,
      selectedItemId: selectedId,
      onBack: () => {
        // Remove submenu and bring back main menu
        toolsSubMenuAPI?.destroy();
        toolsSubMenuAPI = null;
        toolsMainMenuAPI = createToolsMainMenuInContainer(container, {
          onModeSelect: handleModeChange,
          selectedMode: currentMode,
        });
        onLevelChange?.("main", currentMode);
      },
      onSelect: (itemId, meta) => {
        console.log(`Tools: ${mode} → ${itemId}`);
        const now = Date.now();
        const isRepeatSelection =
          mode === lastSelected.mode && lastSelected.subtool === itemId;
        if (
          itemId === lastSelectId &&
          now - lastSelectAt < SELECT_DEBOUNCE_MS &&
          !isRepeatSelection
        ) {
          return;
        }
        lastSelectId = itemId;
        lastSelectAt = now;
        if (mode === "sketch" && lastSelected.mode === mode) {
          if (eraserIds.has(itemId) && lastSelected.subtool === itemId) {
            if (!meta?.wasSelected) {
              lastSelected = { mode, subtool: itemId };
              onToolSelect?.(mode, itemId);
              return;
            }
            eraserSubtoolId =
              eraserSubtoolId === "delete" ? "delete-clean" : "delete";
            const nextId = eraserSubtoolId;
            toolsSubMenuAPI?.setItems(getSubMenuItems(mode));
            toolsSubMenuAPI?.setSelectedItem(nextId);
            lastSelected = { mode, subtool: nextId };
            onToolSelect?.(mode, nextId);
            return;
          }
          if (selectionIds.has(itemId) && lastSelected.subtool === itemId) {
            if (!meta?.wasSelected) {
              lastSelected = { mode, subtool: itemId };
              onToolSelect?.(mode, itemId);
              return;
            }
            selectionSubtoolId =
              selectionSubtoolId === "marquee" ? "lasso" : "marquee";
            const nextId = selectionSubtoolId;
            toolsSubMenuAPI?.setItems(getSubMenuItems(mode));
            toolsSubMenuAPI?.setSelectedItem(nextId);
            lastSelected = { mode, subtool: nextId };
            onToolSelect?.(mode, nextId);
            return;
          }
        }
        lastSelected = { mode, subtool: itemId };
        onToolSelect?.(mode, itemId);
      },
      onItemLongPress: (itemId, anchor) => {
        onToolSettingsRequest?.(mode, itemId, anchor);
      },
    });

    // Ensure visual selection reflects chosen id
    try {
      if (toolsSubMenuAPI && selectedId) {
        toolsSubMenuAPI.setSelectedItem(selectedId);
      }
    } catch {}

    onLevelChange?.("sub", mode);

    // Do not emit default selection here; UI will reflect via store/UIState subscribers
  };

  /**
   * Set the current mode
   */
  const setMode = (mode: ToolMode): void => {
    if (isDestroyed) return;

    currentMode = mode;
    const hasSubMenu = mode === "sketch" || mode === "edit";
    if (!hasSubMenu) {
      ensureMainMenuVisible(mode);
      return;
    }

    if (!toolsMainMenuAPI) {
      toolsMainMenuAPI = createToolsMainMenuInContainer(container, {
        onModeSelect: handleModeChange,
        selectedMode: mode,
      });
      return;
    }

    const mainMenuEl = toolsMainMenuAPI.getElement();
    if (!container.contains(mainMenuEl)) {
      container.appendChild(mainMenuEl);
    }
    toolsMainMenuAPI.setSelectedMode(mode);
  };

  const setModeAndTool = (mode: ToolMode, subtool?: string | null): void => {
    if (isDestroyed) return;
    // Update mode if needed and ensure submenu is visible for modes with tools
    if (mode !== currentMode) {
      setMode(mode);
      if (mode === "sketch" || mode === "edit") {
        showSubMenuForMode(mode);
      }
    } else if ((mode === "sketch" || mode === "edit") && !toolsSubMenuAPI) {
      showSubMenuForMode(mode);
    }
    // Apply subtool selection when provided
    if (typeof subtool === "string" && toolsSubMenuAPI) {
      if (mode === "sketch") {
        if (eraserIds.has(subtool)) {
          eraserSubtoolId = subtool as "delete" | "delete-clean";
          toolsSubMenuAPI.setItems(getSubMenuItems(mode));
        } else if (selectionIds.has(subtool)) {
          selectionSubtoolId = subtool as "marquee" | "lasso";
          toolsSubMenuAPI.setItems(getSubMenuItems(mode));
        }
      }
      lastSelected = { mode, subtool } as any;
      toolsSubMenuAPI.setSelectedItem(subtool);
    }
  };

  /**
   * Get the current mode
   */
  const getMode = (): ToolMode => {
    return currentMode;
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Destroy main menu component
    toolsMainMenuAPI?.destroy();
    toolsMainMenuAPI = null;
    toolsSubMenuAPI?.destroy();
    toolsSubMenuAPI = null;

    // Remove from DOM if still connected
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  };

  // Initialize the component
  initToolsControls();

  return {
    getElement: () => container,
    setMode,
    setModeAndTool,
    getMode,
    destroy,
  };
};

/**
 * Creates ToolsControls component and appends to container
 * @param container - Container element to append to
 * @param config - Configuration for the tools controls
 * @returns ToolsControls API
 */
export const createToolsControlsInContainer = (
  container: HTMLElement,
  config: ToolsControlsConfig = {}
): ToolsControlsAPI => {
  const toolsControls = createToolsControls(config);
  container.appendChild(toolsControls.getElement());
  return toolsControls;
};
